import * as models from 'smg/admin/models';
import Autocomplete from 'smg/autocomplete';
import axios from 'smg/axios';
import * as html from 'smg/html';
import { Phone } from 'smg/phone';
import * as template from 'smg/template';
import { Debounce } from 'smg/util';
import * as realms from '../realms';

interface cartCustomer {
    user: models.User;
    // companyID/realm of the cart item that surfaced this user (quoteRequest's
    // companyID for quote items, order's companyID for order items). It is the
    // realm we operate in for this customer, not necessarily user.companyID.
    companyID: string;
}

export interface ContactDeps {
    // Fires with the canonical userID for downstream lookups (e.g. shipping
    // options). Realm is only used to scope email→user resolution; once we
    // have a userID, realm is irrelevant to the customer's data.
    onCustomerChange?: (userID: string) => void;
    // Fires whenever the realm select's value lands at a new companyID —
    // either from a manual change or from applyRealm's deferred assignment
    // after the realm options finish loading. The place-order button needs
    // this because applyRealm doesn't dispatch 'change' (programmatic
    // `.value =` never does), so the page can't otherwise tell when the
    // realm picked from the cart customer has actually been applied.
    onRealmChange?: (companyID: string) => void;
    // Fires on every email keystroke. onCustomerChange dedupes on userID —
    // typing from one unresolved email to another publishes '' both times,
    // so it never fires — but the place button's readiness depends on the
    // raw email too (a brand-new customer has no userID; the server
    // resolves-or-creates from the email at submit).
    onDraftChange?: () => void;
}

export interface NewCustomerDraft {
    // True (the default) means the created user's name & company come from
    // the shipping address; false means the admin untoggled the switch and
    // typed them into the explicit first/last/company fields below it.
    useShippingName: boolean;
    firstName: string;
    lastName: string;
    company: string;
}

export interface ContactHandle {
    // Trimmed/lowercased contents of the email input — the customer identity
    // the place handler submits when no existing user resolved, so the
    // server can create the user under the selected realm.
    email(): string;
    // E.164 contact phone for that created user, or '' when the input is
    // empty. Async because intlTelInput formats via its utils.js promise —
    // same gate the shipping recipient phone awaits.
    phone(): Promise<string>;
    // Snapshot of the new-customer name controls (the "use shipping name"
    // switch + explicit name fields). Only meaningful when no user resolved.
    newCustomer(): NewCustomerDraft;
}

export function SetupContact(cartInit?: models.ICartPreviewResponse, deps?: ContactDeps): ContactHandle {
    const $email = document.getElementById('contact-email') as HTMLInputElement;
    const $phone = document.getElementById('contact-phone') as HTMLInputElement;
    const $realm = document.getElementById('contact-realm') as HTMLSelectElement;
    const $switcher = document.getElementById('contact-customer-switcher') as HTMLElement;
    const $options = document.getElementById('contact-customer-options') as HTMLElement;
    const $customerLink = document.getElementById('contact-customer-link') as HTMLAnchorElement;
    const $newCustomer = document.getElementById('contact-new-customer') as HTMLElement;
    const $useShippingName = document.getElementById('contact-use-shipping-name') as HTMLInputElement;
    const $nameFields = document.getElementById('contact-new-customer-name-fields') as HTMLElement;
    const $firstName = document.getElementById('contact-first-name') as HTMLInputElement;
    const $lastName = document.getElementById('contact-last-name') as HTMLInputElement;
    const $company = document.getElementById('contact-company') as HTMLInputElement;
    const phone = new Phone($phone);

    const cart = cartInit ? new models.CartPreviewResponse(cartInit) : undefined;
    const customers = collectCartCustomers(cart?.cart.cartItems);

    const realmsReady = realms.CreateOptions($realm, { activeOnly: true });

    const pills: HTMLButtonElement[] = [];
    let currentUserID = '';

    function setActivePill($target: HTMLButtonElement | undefined) {
        for (const $pill of pills) {
            $pill.classList.toggle('active', $pill === $target);
        }
    }

    // New-customer block under the email/phone row: the "created when the
    // order is placed" hint plus the name controls (the "use shipping name"
    // switch and the explicit first/last/company fields it reveals). Shown
    // only when a completed lookup found no user for the typed email, so
    // the admin can tell a deliberate new customer from a typo'd existing
    // one. Hidden while typing (pending resolve) and whenever a user
    // resolves.
    function setNewCustomerVisible(show: boolean) {
        $newCustomer.classList.toggle('d-none', !show);
    }

    // The switch picks where the new customer's name comes from: on (the
    // default) reuses the shipping address's name & company; off reveals
    // explicit fields for the admin to set them directly.
    $useShippingName.addEventListener('change', () => {
        $nameFields.classList.toggle('d-none', $useShippingName.checked);
    });

    function setCurrentUser(user: models.User | undefined) {
        // Every publish bumps the resolve seq AND cancels any queued debounce
        // so neither an in-flight /users lookup nor a not-yet-fired debounced
        // one can overwrite the fresher userID this call is asserting (e.g.
        // a debounce queued from typing before a pill click would otherwise
        // fire 300 ms later — possibly before applyRealm's async .then
        // applies the pill's realm — and resolve to a different user).
        resolveSeq++;
        resolveDebounced.cancel();
        const userID = user?.id ?? '';
        // Always update the profile link so the same userID coming through
        // a richer source (autocomplete pick has name/company; email resolve
        // has just id) refreshes the URL's slug. The early return below would
        // skip that refresh.
        if (user && userID) {
            $customerLink.href = user.iapURL();
            $customerLink.classList.remove('d-none');
            setNewCustomerVisible(false);
        } else {
            $customerLink.classList.add('d-none');
            $customerLink.removeAttribute('href');
        }
        if (userID === currentUserID) {
            return;
        }
        currentUserID = userID;
        deps?.onCustomerChange?.(userID);
    }

    // Autocomplete pick calls with overwriteEmail=false because the
    // autocomplete library has already written the picked value into $email
    // via its `value:` callback. Initial cart load and pill clicks pass true
    // because the cart/pill customer is the authoritative identity for this
    // checkout — keeps email-shown and currentUserID-published in lock-step
    // even if a browser ignored our autocomplete=off and autofilled a stale
    // address.
    function applyCustomer(user: models.User, overwriteEmail: boolean) {
        if (user.email && (overwriteEmail || !$email.value)) {
            $email.value = user.email;
            // Programmatic .value = doesn't fire input/change, so a previous
            // ReportValidity pass on this field stays flagged red. Tell the
            // form helper we've filled it so the is-invalid marker drops.
            $email.dispatchEvent(new Event('clearinvalid'));
        }
        // Always write the phone — including an empty string — so switching
        // from a customer with a phone to one without doesn't leave the old
        // number behind. setNumber doesn't fire input either, so signal the
        // clear ourselves once the iti utils have loaded and the value lands.
        const num = user.phone;
        void phone.ready().then(() => {
            phone.iti.setNumber(num);
            $phone.dispatchEvent(new Event('clearinvalid'));
        });
    }

    function applyRealm(companyID: string) {
        if (!companyID) {
            return;
        }
        // Realm options load asynchronously — defer the assignment so we don't
        // silently set .value to a string that's not yet a registered option.
        void realmsReady.then(() => {
            $realm.value = companyID;
            // Programmatic value sets don't fire 'change', so tell anyone
            // who cares (place-order button readiness) that the realm
            // has actually landed.
            deps?.onRealmChange?.(companyID);
        });
    }

    // Emails are unique per network in our user table, so scoping the user
    // lookup by network (not realm) makes the same customer resolve identically
    // across realms within the same network.
    let resolveSeq = 0;
    async function resolveUserFromEmail() {
        const mySeq = ++resolveSeq;
        const email = $email.value.trim();
        const networkID = realms.SelectedNetworkID($realm);
        if (!email || !networkID) {
            setNewCustomerVisible(false);
            setCurrentUser(undefined);
            return;
        }
        try {
            const resp = await axios.get('/users', {
                params: {
                    'q': `email:${JSON.stringify(email)} network:${networkID}`,
                    // firstName/lastName/company are slug inputs for the IAP
                    // profile link — id alone resolves the URL but the slug
                    // makes it nicer in the bar.
                    'data': 'id,firstName,lastName,company',
                    'max': 1,
                },
            });
            if (mySeq !== resolveSeq) {
                return;
            }
            const users = models.UsersHyejooResponse.fromJSON(resp.data).users;
            // A completed lookup with no match is the definitive "this email
            // will create a new customer" signal.
            setNewCustomerVisible(!users[0]);
            setCurrentUser(users[0]);
        } catch {
            if (mySeq !== resolveSeq) {
                return;
            }
            // Don't claim "new customer" on a failed lookup — we don't know.
            setNewCustomerVisible(false);
            setCurrentUser(undefined);
        }
    }

    const resolveDebounced = Debounce(() => void resolveUserFromEmail(), 300, false);

    $email.addEventListener('input', () => {
        // Clear the resolved user so stale shipping cards don't linger
        // through the debounce + roundtrip; setCurrentUser also bumps the
        // resolve seq, invalidating any in-flight lookup from a prior cycle.
        setActivePill(undefined);
        // Pending resolve — don't show "new customer" mid-typing.
        setNewCustomerVisible(false);
        setCurrentUser(undefined);
        resolveDebounced();
        deps?.onDraftChange?.();
    });
    $realm.addEventListener('change', () => {
        // Re-resolve against the new realm's network. Within-network realm
        // changes resolve to the same userID (per-network email uniqueness)
        // so they're effectively a no-op; cross-network changes correctly
        // resolve to that network's user (or clear). Also covers the case
        // where the admin typed an email before picking a realm.
        setActivePill(undefined);
        setCurrentUser(undefined);
        void resolveUserFromEmail();
        deps?.onRealmChange?.($realm.value);
    });

    if (customers.length > 0) {
        applyCustomer(customers[0].user, true);
        applyRealm(customers[0].companyID);
        setCurrentUser(customers[0].user);

        if (customers.length > 1) {
            const f = document.createDocumentFragment();
            for (const cc of customers) {
                const $btn = template.Clone('#contact-customer-pill-template').firstElementChild as HTMLButtonElement;
                $btn.textContent = cc.user.fullNameWithCompany() || cc.user.email || 'Customer';
                if (cc.user.email) {
                    $btn.title = cc.user.email;
                }
                $btn.addEventListener('click', () => {
                    setActivePill($btn);
                    applyCustomer(cc.user, true);
                    applyRealm(cc.companyID);
                    setCurrentUser(cc.user);
                });
                pills.push($btn);
                f.appendChild($btn);
            }
            $options.replaceChildren(f);
            $switcher.classList.remove('d-none');
            setActivePill(pills[0]);
        }
    }

    new Autocomplete<models.User>({
        input: $email,
        get: async v => {
            // Scope to the selected realm's network — emails are unique per
            // network, so a network-scoped search is precise without locking
            // the admin into a single realm under that network.
            const networkID = realms.SelectedNetworkID($realm);
            if (!networkID) {
                return [];
            }
            return axios.get('/users', {
                params: {
                    'q': `email:${JSON.stringify(`${v.trim()}*`)} network:${networkID}`,
                    'data': 'id,firstName,lastName,email,phone',
                    'sort': 'lastContact desc',
                    'max': 10,
                },
            }).then(resp => models.UsersHyejooResponse.fromJSON(resp.data).users);
        },
        draw: user => html.FromString(`<div>
            <div class="fw-semibold">${html.Escape(user.email ?? '')}</div>
            <div class="small text-muted">${html.Escape([user.fullName(), user.phone].filter(Boolean).join(' · '))}</div>
        </div>`).firstElementChild as HTMLElement,
        value: user => user.email ?? '',
        change: user => {
            setActivePill(undefined);
            applyCustomer(user, false);
            setCurrentUser(user);
        },
    });

    return {
        email: () => $email.value.trim().toLowerCase(),
        phone: async () => {
            // Await the utils gate first so a fast submit can't observe a
            // half-formatted value — same pattern as shipping's snapshot.
            await phone.ready();
            if (!$phone.value.trim()) {
                return '';
            }
            return phone.format();
        },
        newCustomer: () => ({
            useShippingName: $useShippingName.checked,
            firstName: $firstName.value.trim(),
            lastName: $lastName.value.trim(),
            company: $company.value.trim(),
        }),
    };
}

function collectCartCustomers(cartItems?: models.CartItem[]): cartCustomer[] {
    const seen = new Set<string>();
    const out: cartCustomer[] = [];
    for (const item of cartItems ?? []) {
        const user = item.quote?.user ?? item.order?.user;
        if (!user?.id || seen.has(user.id)) {
            continue;
        }
        const companyID = item.quote?.quoteRequest?.companyID ?? item.order?.companyID ?? '';
        seen.add(user.id);
        out.push({ user, companyID });
    }
    return out;
}
